import Auth from '../services/auth';
import axios from 'axios';
import { API_ROOT } from './apiConfig';
const endpoint = `${API_ROOT}/api/v1/Transcriptions`;
const endpointWithAppName = `${API_ROOT}/api/v1/SnChart/Transcriptions`;
const auth = new Auth();


export const uploadInboxDocument = async (formData) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    return await axios.post(`${endpoint}/UploadInboxDocument`, formData, config );
}

export const uploadCodingDocument = async (formData) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    return await axios.post('/api/uploads/coding', formData, config );
}

export const uploadTranscriptionDocument = async (formData) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    return await axios.post(`${API_ROOT}/api/v1/Aideo/Transcription/Ocr`, formData, config );
}

const CONTENT_TYPE_EXTENSIONS = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'image/tiff': '.tif',
    'image/tif': '.tif',
    'application/rtf': '.rtf',
    'text/rtf': '.rtf',
    'text/plain': '.txt'
};

// Cross-origin: the browser only exposes Content-Disposition to JS when the WebApi lists it in
// Access-Control-Expose-Headers, so the header is preferred but the row's own file name is the
// fallback, and the content type (a CORS-safelisted header) supplies an extension as a last resort.
const parseDispositionFileName = (disposition) => {
    if (!disposition) {
        return null;
    }
    const encoded = disposition.match(/filename\*\s*=\s*(?:[^']*)'[^']*'([^;]+)/i);
    if (encoded && encoded[1]) {
        try {
            return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
            // fall through to the plain filename parameter
        }
    }
    const quoted = disposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quoted && quoted[1]) {
        return quoted[1];
    }
    const bare = disposition.match(/filename\s*=\s*([^;]+)/i);
    return bare && bare[1] ? bare[1].trim() : null;
};

const sanitizeFileName = (name) => {
    if (!name) {
        return '';
    }
    // eslint-disable-next-line no-control-regex
    const cleaned = String(name).replace(/[\u0000-\u001f\u007f]/g, '').replace(/[\\/]+/g, '_').trim();
    return cleaned === '.' || cleaned === '..' ? '' : cleaned;
};

const extensionForContentType = (contentType) => {
    const mime = (contentType || '').split(';')[0].trim().toLowerCase();
    return CONTENT_TYPE_EXTENSIONS[mime] || '';
};

// Downloads a Failed row's original document. responseType 'blob' + a synthetic anchor click is
// needed (not a plain <a href>) because the request has to carry the bearer token.
export const downloadOcrDocumentOriginal = async (id, accountNumber, fallbackFileName) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        },
        responseType: 'blob'
    }
    const response = await axios.get(`${API_ROOT}/api/v1/Aideo/OcrDocumentUploads/${id}/OriginalDocument?accountNumber=${encodeURIComponent(accountNumber)}`, config);
    const headers = response.headers || {};
    const contentType = headers['content-type'];
    const fileName = sanitizeFileName(parseDispositionFileName(headers['content-disposition']))
        || sanitizeFileName(fallbackFileName)
        || `original-document${extensionForContentType(contentType)}`;
    const data = response.data;
    const blob = (typeof Blob !== 'undefined' && data instanceof Blob && data.type)
        ? data
        : new Blob([data], contentType ? { type: contentType } : undefined);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

// Transcription "Preview only": the Word document one record's job would have had (Stage, allowlisted
// users only; the WebApi checks). page is the record's first page.
export const downloadTranscriptionPreview = async (id, accountNumber, page) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        },
        responseType: 'blob'
    }
    const response = await axios.get(`${API_ROOT}/api/v1/Aideo/OcrDocumentUploads/${id}/Preview?accountNumber=${encodeURIComponent(accountNumber)}&page=${encodeURIComponent(page)}`, config);
    const headers = response.headers || {};
    const fileName = sanitizeFileName(parseDispositionFileName(headers['content-disposition'])) || `preview-${id}-page-${page}.doc`;
    const data = response.data;
    const blob = (typeof Blob !== 'undefined' && data instanceof Blob && data.type) ? data : new Blob([data], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const checkRedXForJob = async (account, reportId) => {
    const config = {
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    return await axios.get(`${endpointWithAppName}/CheckRedX/account/${account}/reportId/${reportId}`,config);
}

export const getOcrDocumentUploads = async (getUploadRequest) => {
    const config = {
        timeout: 30000,
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    return await axios.post(`${API_ROOT}/api/v1/Aideo/OcrDocumentUploads/Search`, getUploadRequest, config);
}

// "Resubmit for manual processing" -- 998 staff only (the WebApi enforces it): removes every job the
// upload created (coded, exported and billed jobs too; already-removed jobs are skipped) and sends the
// original document to the account's Inbox for manual processing. reasonCode is one of the fixed codes
// (RESUBMIT_REASONS in BatchCodingUploadTable); details are optional plain text (required for Other,
// 500 characters max). A refusal comes back as 409, an invalid reason as 400, both with a PHI-free
// { Reason, Message }; a replay after success returns the same InboxDocumentId.
export const returnUploadToInbox = async (id, accountNumber, { reasonCode, details } = {}) => {
    const config = {
        timeout: 120000,
        headers: {
            "Authorization": `Bearer ${auth.getToken()}`
        }
    }
    const body = { ReasonCode: reasonCode, Details: details || null };
    return await axios.post(`${API_ROOT}/api/v1/Aideo/OcrDocumentUploads/${id}/ReturnToInbox?accountNumber=${encodeURIComponent(accountNumber)}`, body, config);
}

export const resubmitUploadForManualProcessing = returnUploadToInbox;

export const getUploadPolicy = account => axios.get(`${API_ROOT}/api/v1/Aideo/UploadPolicy`, { params: { accountNumber: account }, headers: { Authorization: `Bearer ${auth.getToken()}` } });



// WEBPACK FOOTER //
// ./src/services/transcriptionService.js